import { useEffect, useRef, useState } from 'react';
import { Loader2, Play, Video } from 'lucide-react';
import { Button } from '@/shared/widgets/button';
import { cn } from '@/shared/utils/cn';
import { WishlistIcon } from '@/shared/icons/icons';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useVideoCallCart } from '@/features/video-call/presentation/hooks/useVideoCallCart';
import ProductVideoModal from './ProductVideoModal';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';

export interface ProductCardProps {
  /** Product/design item – use ListingDesignItem from any source (browse, search, recently-viewed) */
  product: ListingDesignItem;
  /** Called before navigating to PDP; pass designId for PLP back-restore anchor (mobile). */
  onBeforeNavigate?: (designId: number) => void;
  /** When true, open PDP in a new tab instead of the same window. */
  openInNewTab?: boolean;
  /** Analytics: called when user clicks the product card or View Details button */
  onProductClick?: (product: ListingDesignItem) => void;
  /** Analytics: eventLogger on wishlist icon tap (before API), browse PLP only in old-react */
  onWishlistClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user toggles the wishlist icon (after successful toggle) */
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
  /** Analytics: called when user clicks the video play button */
  onVideoClick?: (product: ListingDesignItem, productPosition: number) => void;
  /** Analytics: called when user clicks the View Details button specifically */
  onViewDetailsClick?: (product: ListingDesignItem) => void;
  onVideoCallCartAdd?: (product: ListingDesignItem, errorMsg?: string) => void;
  /** When true, hides the Book Video Call button (e.g. on goldcoins PLP). */
  hideVideoCallButton?: boolean;
  /** When true, hides the View Details button completely (for mobile & desktop). */
  hideViewDetailsButton?: boolean;
  /** Desktop only: when true, auto-play the product video inline (every 5th card). */
  autoPlayVideo?: boolean;
  /** 1-based product position in the listing grid (eventLogger). */
  productPosition?: number;
  /** Optional custom badge rendered in the relative image container */
  badge?: React.ReactNode;
  /** Optional custom footer element (e.g. Add to Cart button) */
  footerCta?: React.ReactNode;
  /** Optional extra wrapper class name */
  className?: string;
}

export function ProductCard({
  product,
  onBeforeNavigate,
  openInNewTab,
  onProductClick,
  onWishlistClick,
  onWishlistToggle,
  onVideoClick,
  onViewDetailsClick,
  onVideoCallCartAdd,
  autoPlayVideo,
  productPosition,
  hideVideoCallButton = false,
  hideViewDetailsButton = false,
  badge,
  footerCta,
  className,
}: ProductCardProps) {
  const [videoOpen, setVideoOpen] = useState(false);
  const [heartAnimating, setHeartAnimating] = useState(false);
  const { isWishlisted, toggleWishlist } = useWishlistToggle();
  const { addToVideoCallCart, isLoading: isVideoCallLoading } = useVideoCallCart();
  const isDesktop = useIsDesktop();
  const shouldAutoPlay = Boolean(autoPlayVideo && isDesktop && product.videoUrl);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { ref: autoPlayRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.5,
    enabled: shouldAutoPlay,
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldAutoPlay) return;
    if (isIntersecting) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isIntersecting, shouldAutoPlay]);

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartAnimating(true);
    if (productPosition != null) {
      onWishlistClick?.(product, productPosition);
    }
    const action = isWishlisted(product.designId) ? 'remove' : 'add';
    const success = await toggleWishlist(product.designId);
    if (success) {
      onWishlistToggle?.(product, action);
    }
  };

  const pdpUrl = product.productPageUrl
    ? product.productPageUrl.startsWith('http')
      ? product.productPageUrl
      : product.productPageUrl.startsWith('/')
        ? product.productPageUrl
        : `/${product.productPageUrl.replace(/^\/+/, '')}`
    : undefined;

  const navigateToPdp = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!pdpUrl) return;
    onProductClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) {
      window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = pdpUrl;
    }
  };

  const handleViewDetailsClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!pdpUrl) return;
    onViewDetailsClick?.(product);
    onBeforeNavigate?.(product.designId);
    if (openInNewTab) {
      window.open(pdpUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = pdpUrl;
    }
  };

  const wishlisted = isWishlisted(product.designId);

  return (
    <article
      ref={autoPlayRef}
      className={cn(
        "group flex h-full flex-col overflow-hidden rounded-md border border-gray-200 bg-white",
        className
      )}
    >
      <div className="relative aspect-square bg-[#F9F9FB]">
        {badge}
        {/* Wishlist button — top-left */}
        <button
          type="button"
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm"
          onClick={handleWishlistClick}
        >
          <WishlistIcon
            filled={wishlisted}
            className={cn(
              'h-4 w-4 transition-colors',
              wishlisted ? 'text-red-500' : 'text-gray-400',
              heartAnimating && 'animate-heart-pop',
            )}
            aria-hidden
            onAnimationEnd={() => setHeartAnimating(false)}
          />
        </button>

        {/* Video play button — top-right, only when product has a video */}
        {product.videoUrl && (
          <button
            type="button"
            aria-label="Play product video"
            className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              if (productPosition != null) {
                onVideoClick?.(product, productPosition);
              }
              setVideoOpen(true);
            }}
          >
            <Play className="h-3.5 w-3.5 text-gray-600" aria-hidden />
          </button>
        )}

        {product.imageUrl ? (
          <button
            type="button"
            onClick={navigateToPdp}
            className="relative block h-full w-full"
            aria-label={product.designName}
          >
            <img
              src={product.imageUrl}
              alt={product.designName}
              className="aspect-square h-full w-full object-cover"
              loading="lazy"
            />
            {product.hoverImageUrl && (
              <img
                src={product.hoverImageUrl}
                alt=""
                aria-hidden
                className="absolute inset-0 aspect-square h-full w-full object-cover opacity-0 transition-opacity duration-300 md:group-hover:opacity-100"
                loading="lazy"
              />
            )}
            {shouldAutoPlay && (
              <video
                ref={videoRef}
                src={product.videoUrl!}
                poster={product.imageUrl}
                muted
                loop
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
                aria-hidden
              />
            )}
          </button>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
            No image
          </div>
        )}

        {/* Book Video Call hover overlay — desktop only */}
        {product.customizationId != null && !hideVideoCallButton && (
          <button
            type="button"
            className={cn(
              'pointer-events-none absolute bottom-3 right-3 z-10 hidden items-center justify-center gap-1.5 rounded border border-[#5ba10f] bg-white/95 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-[#5ba10f] opacity-0 shadow transition-opacity duration-200 md:flex md:group-hover:pointer-events-auto md:group-hover:opacity-100',
            )}
            disabled={isVideoCallLoading}
            onClick={async (e) => {
              e.stopPropagation();
              const res = await addToVideoCallCart(product.customizationId!);
              onVideoCallCartAdd?.(product, res.success ? undefined : res.message);
            }}
          >
            {isVideoCallLoading
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              : <Video className="h-3.5 w-3.5" aria-hidden />}
            Book Video Call
          </button>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col px-3 pb-3 pt-2">
        <h2 className="mb-1 min-w-0 truncate text-xs font-medium text-[rgb(153,153,153)]">
          <a
            href={pdpUrl}
            title={product.designName}
            className="block truncate"
            onClick={(e) => { e.preventDefault(); navigateToPdp(); }}
          >
            {product.designName}
          </a>
        </h2>

        <div className="mb-1 flex items-baseline gap-1 text-sm">
          <span className="text-[16px] md:text-[20px] font-semibold text-gray-900">
            {product.discountedPrice || product.price}
          </span>
          {product.discountedPrice &&
            product.price &&
            product.discountedPrice !== product.price ? (
              <span className="text-[12px] md:text-[14px] text-[#9b9b9b] line-through">
                {product.price}
              </span>
            ) : (
              <span className="text-[12px] md:text-[14px] text-transparent select-none">
                &nbsp;
              </span>
            )}
        </div>

        <p className="mb-2 min-w-0 truncate text-[11px] font-bold leading-normal tracking-[-0.03em] text-[#E26A5E]">
          {getDiscountText(product) || '\u00A0'}
        </p>

        {footerCta ? (
          <div className="mt-auto pt-1">{footerCta}</div>
        ) : (
          (!hideViewDetailsButton || (product.customizationId != null && !hideVideoCallButton)) && (
            <div className="mt-auto flex items-center gap-1.5 md:hidden">
              {product.customizationId != null && !hideVideoCallButton && (
                <button
                  type="button"
                  aria-label="Add to video call cart"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-[#B7C7E0] text-[#4A607F] disabled:opacity-50"
                  disabled={isVideoCallLoading}
                  onClick={async () => {
                    const res = await addToVideoCallCart(product.customizationId!);
                    onVideoCallCartAdd?.(product, res.success ? undefined : res.message);
                  }}
                >
                  {isVideoCallLoading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[#5ba10f]" aria-hidden />
                    : <Video className="h-3.5 w-3.5 text-[#5ba10f]" aria-hidden />}
                </button>
              )}
              {!hideViewDetailsButton && (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  aria-label="View details"
                  className="w-full min-w-0 border-[#B7C7E0] text-[11px] font-semibold uppercase tracking-wide text-[#4A607F]"
                  onClick={handleViewDetailsClick}
                >
                  <span className="min-[380px]:hidden">View</span>
                  <span className="hidden min-[380px]:inline">View Details</span>
                </Button>
              )}
            </div>
          )
        )}
      </div>

      {product.videoUrl && (
        <ProductVideoModal
          open={videoOpen}
          onClose={() => setVideoOpen(false)}
          videoUrl={product.videoUrl}
          videoPosterUrl={product.videoPosterUrl}
          productName={product.designName}
          productPageUrl={product.productPageUrl}
        />
      )}
    </article>
  );
}

function getDiscountText(
  product: Pick<
    ListingDesignItem,
    'discountName' | 'discountValue' | 'discountPercent'
  >
): string {
  // If API provides a custom offer label (e.g. "70% off on Making Charges"), use it directly
  if (product.discountName) {
    return product.discountName;
  }

  // Fallback: "Save ₹X" style text when there is a numeric discount but no named offer
  if (product.discountPercent && product.discountPercent > 0 && product.discountValue) {
    return `Save ${product.discountValue}`;
  }

  return '';
}

